import {createAction, createReducer} from '@reduxjs/toolkit';
import {
    fetchEventTypesRequest,
    fetchEventTypesSuccess,
    fetchEventTypesFailure,
    fetchStatusesRequest,
    fetchStatusesSuccess,
    fetchStatusesFailure,
    fetchEventDetailRequest,
    fetchEventDetailSuccess,
    fetchEventDetailFailure,
} from '/src/openapi/ccdb-bff/apis/EventDetailsApi';
import type {EventType} from '/src/openapi/ccdb-bff/models/EventType';
import type {EventStatus} from '/src/openapi/ccdb-bff/models/EventStatus';
import type {EventDetail} from '/src/openapi/ccdb-bff/models/EventDetail';
import type {CcdbCompany} from '/src/openapi/ccdb-bff/models/CcdbCompany';

// ──────── Types ────────

export type DateConfidence = 'Estimated' | 'Approximate' | 'Confirmed';

export type EventDetailsFormData = {
    companyId: string;
    companyName: string;
    eventName: string;
    eventStandardName: string;
    eventType: string;
    effectiveBeginDate: string;
    effectiveBeginTime: string;
    dateConfidence: DateConfidence;
    effectiveEndDate: string;
    effectiveEndTime: string;
    fiscalYearEnd: string;
    eventYear: string;
    eventDetailUrl: string;
    webcastUrl: string;
    tollFree: string;
    tollNumber: string;
    passCode: string;
    replayTollFree: string;
    replayTollNumber: string;
    replayPassCode: string;
    isReplayAvailable: string;
    replayExpiryDate: string;
    country: string;
    timeZone: string;
    validationSource: string;
    infoType: string;
    status: string;
    notes: string;
    screenshotFile: File | null;
    creationDate: string;
    holidayRemark: string;
};

export type EventDetailsState = {
    formData: EventDetailsFormData;
    saving: boolean;
    error: string | null;
    successMessage: string | null;
    eventTypes: EventType[];
    eventTypesLoading: boolean;
    statuses: EventStatus[];
    statusesLoading: boolean;
    companySearchResults: CcdbCompany[];
    companySearchLoading: boolean;
    eventDetailLoading: boolean;
    editingEventId: string | null;
    fetchedEventDetail: EventDetail | null;
};

// ──────── Initial State ────────

const initialFormData: EventDetailsFormData = {
    companyId: '',
    companyName: '',
    eventName: '',
    eventStandardName: '',
    eventType: '',
    effectiveBeginDate: '',
    effectiveBeginTime: '',
    dateConfidence: 'Estimated',
    effectiveEndDate: '',
    effectiveEndTime: '',
    fiscalYearEnd: '',
    eventYear: '',
    eventDetailUrl: '',
    webcastUrl: '',
    tollFree: '',
    tollNumber: '',
    passCode: '',
    replayTollFree: '',
    replayTollNumber: '',
    replayPassCode: '',
    isReplayAvailable: '',
    replayExpiryDate: '',
    country: 'United States',
    timeZone: '',
    validationSource: '',
    infoType: '',
    status: 'Active',
    notes: '',
    screenshotFile: null,
    creationDate: new Date().toLocaleDateString('en-US', {month: '2-digit', day: '2-digit', year: 'numeric'}),
    holidayRemark: '',
};

const initialState: EventDetailsState = {
    formData: {...initialFormData},
    saving: false,
    error: null,
    successMessage: null,
    eventTypes: [],
    eventTypesLoading: false,
    statuses: [],
    statusesLoading: false,
    companySearchResults: [],
    companySearchLoading: false,
    eventDetailLoading: false,
    editingEventId: null,
    fetchedEventDetail: null,
};

// ──────── Actions ────────

export const updateEventField = createAction<{field: keyof EventDetailsFormData; value: string | File | null}>('CCDB_EVENT_DETAILS__UPDATE_FIELD');
export const resetEventForm = createAction('CCDB_EVENT_DETAILS__RESET_FORM');
export const clearEventMessages = createAction('CCDB_EVENT_DETAILS__CLEAR_MESSAGES');

export const saveEventDetailsRequest = createAction('CCDB_EVENT_DETAILS__SAVE_REQUEST');
export const saveEventDetailsSuccess = createAction<string>('CCDB_EVENT_DETAILS__SAVE_SUCCESS');
export const saveEventDetailsFailure = createAction<string>('CCDB_EVENT_DETAILS__SAVE_FAILURE');

export const setCompanySearchLoading = createAction<boolean>('CCDB_EVENT_DETAILS__COMPANY_SEARCH_LOADING');
export const setCompanySearchResults = createAction<CcdbCompany[]>('CCDB_EVENT_DETAILS__COMPANY_SEARCH_RESULTS');
export const clearCompanySearch = createAction('CCDB_EVENT_DETAILS__CLEAR_COMPANY_SEARCH');

export const populateEventForm = createAction<Partial<EventDetailsFormData>>('CCDB_EVENT_DETAILS__POPULATE_FORM');

// ──────── Reducer ────────

export const ccdbEventDetails = createReducer<EventDetailsState>(initialState, builder => builder
    .addCase(updateEventField, (state, action) => {
        const {field, value} = action.payload;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state.formData as any)[field] = value;
    })
    .addCase(resetEventForm, state => {
        state.formData = {...initialFormData};
        state.error = null;
        state.successMessage = null;
        state.editingEventId = null;
        state.eventDetailLoading = false;
        state.fetchedEventDetail = null;
    })
    .addCase(clearEventMessages, state => {
        state.error = null;
        state.successMessage = null;
    })
    .addCase(saveEventDetailsRequest, state => {
        state.saving = true;
        state.error = null;
        state.successMessage = null;
    })
    .addCase(saveEventDetailsSuccess, (state, action) => {
        state.saving = false;
        state.successMessage = action.payload;
    })
    .addCase(saveEventDetailsFailure, (state, action) => {
        state.saving = false;
        state.error = action.payload;
    })
    // ── Company Search ──
    .addCase(setCompanySearchLoading, (state, action) => {
        state.companySearchLoading = action.payload;
    })
    .addCase(setCompanySearchResults, (state, action) => {
        state.companySearchResults = action.payload;
        state.companySearchLoading = false;
    })
    .addCase(clearCompanySearch, state => {
        state.companySearchResults = [];
        state.companySearchLoading = false;
    })
    // ── Fetch Event Detail by ID ──
    .addCase(fetchEventDetailRequest, (state, action) => {
        state.eventDetailLoading = true;
        state.editingEventId = action.payload.meta?.eventId ?? null;
        state.error = null;
    })
    .addCase(fetchEventDetailSuccess, (state, action) => {
        state.eventDetailLoading = false;
        // Store the raw API response for the hook to map into form fields
        state.fetchedEventDetail = action.payload.data;
    })
    .addCase(fetchEventDetailFailure, (state, action) => {
        state.eventDetailLoading = false;
        state.error = action.payload.data?.message ?? 'Failed to fetch event details';
    })
    .addCase(populateEventForm, (state, action) => {
        Object.assign(state.formData, action.payload);
    })
    // ── Event Types ──
    .addCase(fetchEventTypesRequest, state => {
        state.eventTypesLoading = true;
    })
    .addCase(fetchEventTypesSuccess, (state, action) => {
        state.eventTypesLoading = false;
        state.eventTypes = action.payload.data;
    })
    .addCase(fetchEventTypesFailure, (state, action) => {
        state.eventTypesLoading = false;
        state.error = action.payload.data?.message ?? 'Failed to load event types';
    })
    // ── Statuses ──
    .addCase(fetchStatusesRequest, state => {
        state.statusesLoading = true;
    })
    .addCase(fetchStatusesSuccess, (state, action) => {
        state.statusesLoading = false;
        state.statuses = action.payload.data;
    })
    .addCase(fetchStatusesFailure, (state, action) => {
        state.statusesLoading = false;
        state.error = action.payload.data?.message ?? 'Failed to load statuses';
    }),
);
